import React, {useState, memo, useEffect, useRef, useContext} from "react";
import {useDispatch, useSelector} from "react-redux";
import {resizeViewer} from "utils/builder/IScheduleLogic";
import { Toaster } from 'react-hot-toast';
import { CSSTransition } from "react-transition-group";
import "./DashboardProj.scss";
import { ReactComponent as Control} from "static/icons/double-right.svg";

import DashboardInfoTable from "components/DashboardInfoTable";
import EditorSection from "components/EditorSection";
import SidebarModule from "components/sidebarComponents/SidebarModule";
import UniversalLoader from "components/uiComponents/UniversalLoader";
import {getFoldersCollections, selectCollectionOnView, unselectCollectionOnView} from "../../store/actions/collections";
import {setTabActive, closeTab, ElementsAction} from "../../store/actions/table";
import {ReactComponent as CloseIcon} from "../../static/icons/close-small.svg";
import {useDashboardState} from "../../components/DashboardContext";
import {ModalContext} from "../../components/ModalContext";
import ProofModal from "../../components/modals/ProofModal";
import {tabType} from "../../utils/data";

const DashboardProj = ({
  location,
}) => {

  const dispatch = useDispatch();
  const tabs = useRef();
  const tabsWrapper = useRef();
  const scrollStep = 200;
  const { handleModal } = useContext(ModalContext);

  const {
    table,
    viewer: {
      quickCounterData,
      templates
    },
  } = useSelector((state) => state);
  const { setIsTableHidden } = useDashboardState();

  const [sidebarIsOpened, setSidebarIsOpened] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [activeLeftControl, setActiveLeftControl] = useState(false);
  const [activeRightControl, setActiveRightControl] = useState(false);

  useEffect(() => {
    const tabsEl = tabs.current;
    const tabsWrapperEl = tabsWrapper.current;

    if(tabsEl.scrollWidth > tabsWrapperEl.scrollWidth &&
      tabsEl.scrollWidth - tabsEl.scrollLeft !== tabsEl.clientWidth) {
      setActiveRightControl(true);
    } else {
      setActiveRightControl(false);
    }
    if(tabsEl.scrollLeft > 0) {
      setActiveLeftControl(true);
    } else {
      setActiveLeftControl(false);
    }
  }, [table.tabs.length])

  useEffect(() => {
    dispatch(getFoldersCollections());

    table?.tabs.forEach((tab) => {
      if(tab?.active) {
        setActiveTab(tab);
        
      }
    })

    if(!table.tabs.length) {
      setIsTableHidden(true);
      dispatch(ElementsAction(null));
    }
  }, [table, table?.active]);

  const handleChangeSidebar = status => {
    setSidebarIsOpened(status);

    setTimeout(() => {
      resizeViewer();
    }, 310);
  };

  const handleActiveTab = (tab) => {
    if(!tab.active) {
      dispatch(setTabActive(tab));
      unselectCollectionOnView();

      if(tab.type === tabType.saved) {
        selectCollectionOnView([tab.collection]);
      }
    }
  };

  const handleCloseTab = (event, tab) => {
    if(tab.type === tabType.unsaved) {
      handleModal(
        <ProofModal proofCallback={()=>closeDownTab(event, tab)} cancelButtonText={"Back to editing"} proofButtonText={"Close tab"}>
          <div>You want to close <b>“{tab.name} - unsaved”</b> without saving. All your changes will be discarded.</div>
        </ProofModal>
      );
    } else {
      closeDownTab(event, tab);
    }
  };

  const closeDownTab = (event, tab) => {
    event.stopPropagation();
    dispatch(closeTab(tab));
    unselectCollectionOnView();
    if(tab.type === tabType.unsaved) {
      handleCancel();
    }
  };

  const handleCancel = () => {
    handleModal(null, true);
  };

  const nextTab = () => {
    const tabsEl = tabs.current;

    tabsEl.scroll(tabsEl.scrollLeft + scrollStep, 0);

    if(tabsEl.scrollWidth - tabsEl.scrollLeft === tabsEl.clientWidth) {
      setActiveRightControl(false);
    }
    if(tabsEl.scrollLeft !== 0) {
      setActiveLeftControl(true);
    }
  };

  const prevTab = () => {
    const tabsEl = tabs.current;

    tabsEl.scroll(tabsEl.scrollLeft - scrollStep, 0);

    if(tabsEl.scrollLeft === 0) {
      setActiveLeftControl(false);
    }
    if(tabsEl.scrollWidth - tabsEl.scrollLeft !== tabsEl.clientWidth) {
      setActiveRightControl(true);
    }
  };

  return (
    <div className="dashboard-proj">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            maxWidth: '480px',
            margin: 0,
            padding: 0,
            borderRadius: '20px',
          },
        }}
      />

      <SidebarModule
        isOpened={sidebarIsOpened}
        onChangeSidebar={handleChangeSidebar}
      />

      <div className="main">
        <div className={`main-dashboard ${table.colapsedDash}`}>
          <EditorSection
            autodesk={window.Autodesk}
            projectData={location.state.project}
            quickCounterData={quickCounterData}
            table={table}
            sidebarIsOpened={sidebarIsOpened}
          />
          <div className="tabs-wrapper" ref={tabsWrapper}>
            {
              activeLeftControl &&
              <div className="tabs-control left-control" onClick={prevTab}><Control /></div>
            }
            <div className="tabs" ref={tabs}>
                {
                  table.tabs.map((tab) => {

                    return (
                        <div key={tab.name}
                             className={`tab ${activeTab?.id === tab.id && "tab-active"}`}
                             onClick={()=> handleActiveTab(tab)}>
                          {
                            tab.type === tabType.saved &&
                            <span className="tab__color" style={{backgroundColor: tab.color}}></span>
                          }
                          <span className="tab__text">{tab.name}</span>
                          {
                            tab.type === tabType.unsaved &&
                            <span className="drop-down-add-to__unsaved">&nbsp;- unsaved</span>
                          }
                          <CloseIcon className="tab__close-icon" onClick={(event)=> handleCloseTab(event, tab)}/>
                        </div>
                    )
                  })
                }
            </div>
            {
              activeRightControl &&
              <div className="tabs-control right-control" onClick={nextTab}><Control /></div>
            }
          </div>
          {activeTab &&  <DashboardInfoTable table={activeTab} />}
        </div>

        <CSSTransition
          in={!templates}
          timeout={150}
          unmountOnExit
          classNames="animation"
        >
          <div className="dashboard-proj__preloader">
            <UniversalLoader
              label="Loading..."
              labelColor="gray5"
              preloaderColor="black"
              preloaderBgColor="gray5"
              position="center"
              isWithoutContainerBg
            />
          </div>
        </CSSTransition>

      </div>
    </div>
  );
};

export default memo(DashboardProj);
